import first from "lodash/first";
import { graphql, readInlineData } from "react-relay";

import { article as articleData, section as sectionData } from "shared/data";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import type { helpersCheckIsAmpEnabledArticle$key } from "scmp-app/queries/__generated__/helpersCheckIsAmpEnabledArticle.graphql";

import { ArticleOGImageType, ExcludedArticleIds, type ValueOfArticleOGImageType } from "./consts";

const articleOGTypeMap: Record<string, ValueOfArticleOGImageType> = {
  [articleData.types.analysis.entityId]: ArticleOGImageType.analysis,
  [articleData.types.explainer.entityId]: ArticleOGImageType.explainer,
  [articleData.types.factCheck.entityId]: ArticleOGImageType.factCheck,
  [articleData.types.live.entityId]: ArticleOGImageType.live,
  [articleData.types.obituary.entityId]: ArticleOGImageType.obituary,
  [articleData.types.review.entityId]: ArticleOGImageType.review,
  [articleData.types.series.entityId]: ArticleOGImageType.series,
};

export const resolveArticleOGImageType = (
  articleTypeId: string,
  isScmpEditorialAuthor: boolean,
): undefined | ValueOfArticleOGImageType => {
  const isColumnOrOpinion =
    articleTypeId === articleData.types.column.entityId ||
    articleTypeId === articleData.types.opinion.entityId;
  if (isColumnOrOpinion) {
    return isScmpEditorialAuthor ? ArticleOGImageType.editorial : ArticleOGImageType.opinion;
  }

  return articleOGTypeMap[articleTypeId];
};

export const checkIsAmpEnabledArticle = (reference: helpersCheckIsAmpEnabledArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckIsAmpEnabledArticle on Article @inline {
        entityId
        types {
          value {
            entityId
          }
        }
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
          }
        }
        disableOffPlatformContent
        sponsorType
        published
        ...helpersCheckIsPlusArticle
      }
    `,
    reference,
  );

  const baseArticleTypeData = article.types?.flatMap(type => type?.value) ?? [];

  const baseArticleSectionData = article.sectionsV2?.flatMap(section => section?.value) ?? [];

  const isNotSponsorType = !article.sponsorType;

  const isPublished = article.published;

  const isNotInfographicsArticle = !(
    baseArticleSectionData.some(
      section => section?.entityId === sectionData.infographics.entityId,
    ) || baseArticleTypeData.some(type => type?.entityId === articleData.types.infographic.entityId)
  );

  const isNotSeriesArticle =
    first(baseArticleTypeData)?.entityId !== articleData.types.series.entityId;

  const isNotNewsletterTypeArticle =
    first(baseArticleTypeData)?.entityId !== articleData.types.newsletter.entityId;

  const isNotExcludedArticle = !ExcludedArticleIds.includes(article.entityId);

  const isNotAnnouncementArticle = article.entityId !== articleData.articles.announcement.entityId;

  const isNotDisableAmp = !(article.disableOffPlatformContent ?? []).includes("amp");

  const isNotArticleLiveType =
    first(baseArticleTypeData)?.entityId !== articleData.types.live.entityId;

  const isNotPlusArticle = !checkIsPlusArticle(article);

  return (
    isNotSponsorType &&
    isPublished &&
    isNotInfographicsArticle &&
    isNotSeriesArticle &&
    isNotNewsletterTypeArticle &&
    isNotExcludedArticle &&
    isNotAnnouncementArticle &&
    isNotDisableAmp &&
    isNotArticleLiveType &&
    isNotPlusArticle
  );
};
